import React, { useCallback, useContext } from 'react';
import { connect } from 'react-redux';
import overrideStyle from 'lib/overrideStyle';
import PropTypes from 'prop-types';

import CustomIcon from 'presentation/components/icons/CustomIcon';
import { CustomTextContext } from 'context/CustomTextContext';
import { determineFontColor, shiftColorShade } from 'lib/colorLogic';
import finalizeTimeline from 'actions/selfService/finalizeTimeline';
import getRadius from 'lib/getRadius';
import { getIsThrottled, getWithinOfficeHours, getUnreadMessageCount } from 'app/modules';
import { ICON_TYPES } from 'enums/configuration';
import isMobileWidth from 'lib/isMobileWidth';
import { trackClickContactUs } from 'events/analytics';
import uiEnums from 'enums/ui';
import uiEvents from 'events/ui';
import updateUnreadMessageCount from 'actions/messages/updateUnreadMessageCount';

const FocusWrapper = overrideStyle('span')`
  height: 75px;
  width: 100%;
  &:focus {
    box-shadow: 0 0 0px 2px #81b5ea;
    outline: solid 1px #81b5ea;
  }
`;

// TODO: Remove colors.brandPrimary fallback
// https://app.clubhouse.io/gladly/story/58435
const Wrapper = overrideStyle('button')`
  background: ${p => p.theme.colors.chatLauncher || p.theme.colors.brandPrimary};
  border: ${p => p.theme.colors.chatLauncher || p.theme.colors.brandPrimary} 1px solid;
  border-radius: 0px 0px ${p =>
    p.isMobileWidth || p.theme.isMobileSdk ? '0' : getRadius(p.theme.borderRadius, 8, 16)} ${p =>
  p.isMobileWidth ? '0' : getRadius(p.theme.borderRadius, 8, 16)};
  color: ${p => determineFontColor(p.theme.colors.chatLauncher || p.theme.colors.brandPrimary)};
  cursor: pointer;
  font-family:${p => p.theme.fontLibrary.primary};
  font-size: 14px;
  height: 100%;
  justify-content: center;
  margin-right: 0;
  padding: 0;
  outline: none;
  width: 100%;
  -moz-appearance: none;
  -webkit-appearance: none;
  &:hover, &:focus {
    background: ${p => shiftColorShade(p.theme.colors.chatLauncher || p.theme.colors.brandPrimary, -1)};
    border: 1px dotted;
    border-color: ${shiftColorShade('#767676', -10)};
  }

  &:active {
    background: ${p => shiftColorShade(p.theme.colors.chatLauncher || p.theme.colors.brandPrimary, -2)};
    border: 1px dotted ;
    border-color: ${shiftColorShade('#767676', -10)};
  }
`;

const Message = overrideStyle('div')`
  align-items: center;
  cursor: pointer;
  display: flex;
  font-weight: bold;
  justify-content: center;
  padding-left: 10px;
  stroke: ${p => determineFontColor(p.theme.colors.chatLauncher || p.theme.colors.brandPrimary)};
`;

const IconWrapper = overrideStyle('div')`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: flex-end;
  left: -25px;
  height: 15px;
  position: absolute;
  width: 17px;
`;

const UnreadNotification = overrideStyle('div')`
  background-color: #d4240d;
  border-radius: 8px;
  height: 8px;
  width: 8px;
`;

const MessageText = overrideStyle('span')`
  position: relative;
  cursor: pointer;
`;

function ChatWithAgentButton({
  isThrottled,
  isWithinOfficeHours,
  startChatting,
  showOfficeHourMessage,
  unreadMessageCount,
}) {
  const { buttonText, inactiveButtonText } = useContext(CustomTextContext);
  let text = buttonText;
  const hasUnreadMessages = unreadMessageCount > 0;
  if (!isWithinOfficeHours || isThrottled) {
    text = inactiveButtonText;
  } else if (hasUnreadMessages) {
    text = `${unreadMessageCount} New Message${unreadMessageCount === 1 ? '' : 's'}`;
  }

  const onStartChatting = useCallback(() => startChatting(buttonText), [buttonText]);
  const onShowOfficeHoursMessage = useCallback(() => showOfficeHourMessage(inactiveButtonText), [inactiveButtonText]);
  const onKeyUp = useCallback(evt => evt.key === 'Enter' && startChatting(), [startChatting]);

  return (
    <FocusWrapper>
      <Wrapper
        data-aid="selfService-chatButton"
        isMobileWidth={isMobileWidth()}
        onClick={isWithinOfficeHours && !isThrottled ? onStartChatting : onShowOfficeHoursMessage}
        onKeyUp={onKeyUp}
      >
        <Message>
          <MessageText id="gladlyStartChatButton">
            <IconWrapper>
              {hasUnreadMessages ? <UnreadNotification /> : <CustomIcon type={ICON_TYPES.CHAT} />}
            </IconWrapper>
            {text}
          </MessageText>
        </Message>
      </Wrapper>
    </FocusWrapper>
  );
}

ChatWithAgentButton.propTypes = {
  isThrottled: PropTypes.bool.isRequired,
  isWithinOfficeHours: PropTypes.bool.isRequired,
  showOfficeHourMessage: PropTypes.func.isRequired,
  startChatting: PropTypes.func.isRequired,
  unreadMessageCount: PropTypes.number,
};

export const mapDispatchToProps = dispatch => {
  return {
    startChatting: buttonText => {
      dispatch(uiEvents.navigateForward(uiEnums.LOCATIONS.CHAT));
      dispatch(finalizeTimeline);
      dispatch(updateUnreadMessageCount(0));
      dispatch(trackClickContactUs({ buttonText }));
    },
    initiateSecurePayment: () => {
      dispatch(uiEvents.navigateForward(uiEnums.LOCATIONS.SECURE_PAYMENT));
    },
    showOfficeHourMessage: buttonText => {
      dispatch(uiEvents.navigateForward(uiEnums.LOCATIONS.OFFICE_HOURS));
      dispatch(trackClickContactUs({ buttonText }));
    },
  };
};

export const mapStateToProps = state => {
  return {
    isThrottled: getIsThrottled(state),
    isWithinOfficeHours: getWithinOfficeHours(state),
    unreadMessageCount: getUnreadMessageCount(state),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ChatWithAgentButton);
